import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';

const SecurityTab = () => {
  const { user, signIn, updatePassword } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);

  const loginActivity = [
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'New York, United States',
      ip: '192.168.1.100',
      timestamp: '2026-01-30 05:45:23',
      status: 'current'
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'New York, United States',
      ip: '192.168.1.101',
      timestamp: '2026-01-29 18:32:15',
      status: 'success'
    },
    {
      id: 3,
      device: 'Firefox on macOS',
      location: 'Boston, United States',
      ip: '203.0.113.45',
      timestamp: '2026-01-28 14:20:08',
      status: 'success'
    },
    {
      id: 4,
      device: 'Chrome on Android',
      location: 'Unknown Location',
      ip: '198.51.100.78',
      timestamp: '2026-01-27 09:15:42',
      status: 'failed'
    }
  ];

  const securitySettings = [
    {
      id: 'email-notifications',
      label: 'Email notifications for new logins',
      description: 'Get notified when your account is accessed from a new device',
      enabled: true
    },
    {
      id: 'session-timeout',
      label: 'Automatic session timeout',
      description: 'Log out automatically after 30 minutes of inactivity',
      enabled: true
    },
    {
      id: 'api-access',
      label: 'Allow API access',
      description: 'Enable programmatic access to your account via API',
      enabled: true
    },
    {
      id: 'data-export',
      label: 'Allow data export',
      description: 'Enable downloading your data in CSV/JSON format',
      enabled: true
    }
  ];

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    if (passwordErrors?.[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm?.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordForm?.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm?.newPassword?.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) return;
    setPasswordLoading(true);
    try {
      // Re-authenticate with current password to verify it is correct
      await signIn(user?.email, passwordForm.currentPassword);
      // Now update to the new password
      await updatePassword(passwordForm.newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      toast.success('Password updated successfully');
    } catch (err) {
      const message = err?.message || '';
      if (message.toLowerCase().includes('invalid') || message.toLowerCase().includes('credentials') || message.toLowerCase().includes('wrong')) {
        setPasswordErrors({ currentPassword: 'Current password is incorrect' });
      } else {
        toast.error(message || 'Failed to update password. Please try again.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'current':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md">
            <Icon name="Monitor" size={14} />
            Current Session
          </span>
        );
      case 'success':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-success/10 text-success text-xs font-medium rounded-md">
            <Icon name="CheckCircle2" size={14} />
            Success
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-destructive/10 text-destructive text-xs font-medium rounded-md">
            <Icon name="XCircle" size={14} />
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
          Change Password
        </h2>
        <div className="space-y-4 max-w-2xl">
          <Input
            label="Current Password"
            type="password"
            name="currentPassword"
            value={passwordForm?.currentPassword}
            onChange={handlePasswordChange}
            error={passwordErrors?.currentPassword}
            placeholder="Enter current password"
            required
          />
          <Input
            label="New Password"
            type="password"
            name="newPassword"
            value={passwordForm?.newPassword}
            onChange={handlePasswordChange}
            error={passwordErrors?.newPassword}
            placeholder="Enter new password"
            description="Must be at least 8 characters long"
            required
          />
          <Input
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            value={passwordForm?.confirmPassword}
            onChange={handlePasswordChange}
            error={passwordErrors?.confirmPassword}
            placeholder="Confirm new password"
            required
          />
          <div className="flex items-center gap-3 pt-4">
            <Button
              variant="default"
              size="default"
              onClick={handlePasswordSubmit}
              iconName={passwordLoading ? 'Loader2' : 'Lock'}
              iconPosition="left"
              disabled={passwordLoading}
            >
              {passwordLoading ? 'Updating…' : 'Update Password'}
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Two-Factor Authentication
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="flex items-center gap-2">
            {twoFactorEnabled ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success/10 text-success text-sm font-medium rounded-md">
                <Icon name="ShieldCheck" size={16} />
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-muted-foreground text-sm font-medium rounded-md">
                <Icon name="ShieldOff" size={16} />
                Disabled
              </span>
            )}
          </div>
        </div>

        {!twoFactorEnabled ? (
          <div className="bg-muted/50 rounded-lg p-4 md:p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Smartphone" size={24} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                  Secure Your Account
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password.
                </p>
                <Button
                  variant="default"
                  size="default"
                  onClick={() => setShowTwoFactorSetup(true)}
                  iconName="Shield"
                  iconPosition="left"
                >
                  Enable Two-Factor Authentication
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Smartphone" size={20} color="var(--color-primary)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Authenticator App</p>
                  <p className="text-xs text-muted-foreground">Last used: 2026-01-30</p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Manage
              </Button>
            </div>
            <Button
              variant="destructive"
              size="default"
              onClick={() => setTwoFactorEnabled(false)}
              iconName="ShieldOff"
              iconPosition="left"
            >
              Disable Two-Factor Authentication
            </Button>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
          Security Preferences
        </h2>
        <div className="space-y-4">
          {securitySettings?.map((setting) => (
            <div key={setting?.id} className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
              <Checkbox
                checked={setting?.enabled}
                onChange={() => {}}
                className="mt-1"
              />
              <div className="flex-1">
                <label className="text-sm md:text-base font-medium text-foreground block mb-1">
                  {setting?.label}
                </label>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {setting?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Login Activity
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Recent login attempts and active sessions
            </p>
          </div>
          <Button
            variant="outline"
            size="default"
            iconName="LogOut"
            iconPosition="left"
          >
            Log Out All Devices
          </Button>
        </div>

        <div className="space-y-4">
          {loginActivity?.map((activity) => (
            <div
              key={activity?.id}
              className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 bg-muted/50 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Monitor" size={18} color="var(--color-muted-foreground)" />
                  <h3 className="text-sm md:text-base font-medium text-foreground">
                    {activity?.device}
                  </h3>
                </div>
                <div className="space-y-1 text-xs md:text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} />
                    <span>{activity?.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Globe" size={14} />
                    <span>{activity?.ip}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={14} />
                    <span>{activity?.timestamp}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(activity?.status)}
                {activity?.status !== 'current' && (
                  <Button variant="ghost" size="sm" iconName="Ban">
                    Block
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {showTwoFactorSetup && (
        <div className="fixed inset-0 bg-background/80 z-[1040] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevation-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Enable Two-Factor Authentication
              </h3>
              <button
                onClick={() => setShowTwoFactorSetup(false)}
                className="w-8 h-8 rounded-md hover:bg-muted transition-smooth flex items-center justify-center"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="text-center">
                <div className="w-48 h-48 mx-auto bg-muted rounded-lg flex items-center justify-center mb-4">
                  <Icon name="QrCode" size={120} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Scan this QR code with your authenticator app
                </p>
                <div className="p-3 bg-muted rounded-lg">
                  <code className="text-xs font-mono text-foreground">
                    JBSWY3DPEHPK3PXP
                  </code>
                </div>
              </div>

              <Input
                label="Verification Code"
                type="text"
                placeholder="Enter 6-digit code"
                description="Enter the code from your authenticator app"
              />
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button
                variant="outline"
                size="default"
                onClick={() => setShowTwoFactorSetup(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="default"
                onClick={() => {
                  setTwoFactorEnabled(true);
                  setShowTwoFactorSetup(false);
                }}
                iconName="ShieldCheck"
                iconPosition="left"
              >
                Enable
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;